import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, ActivityIndicator } from 'react-native-paper';
import JobService from '../services/jobService';
import { ErrorLogger } from '../utils/errorLogger';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_BAR_HEIGHT = 80;
const MIN_BAR_HEIGHT = 4;

// JS getDay(): 0=Sun,1=Mon...6=Sat → ISO index: Mon=0...Sun=6
const jsToIso = (jsDay: number) => (jsDay === 0 ? 6 : jsDay - 1);

const PerformanceTrendCard: React.FC = () => {
  const theme = useTheme();
  const [dailyTotals, setDailyTotals] = useState<number[]>(new Array(7).fill(0));
  const [loading, setLoading] = useState(true);
  const todayIso = jsToIso(new Date().getDay());

  const loadData = async () => {
    try {
      setLoading(true);
      const jobs = await JobService.getCompletedJobsThisWeek();
      const totals = new Array(7).fill(0);
      for (const job of jobs) {
        const idx = jsToIso(job.createdAt.getDay());
        totals[idx] += job.amount;
      }
      setDailyTotals(totals);
    } catch (error) {
      ErrorLogger.logError(error as Error, 'PerformanceTrendCard.loadData');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const maxVal = Math.max(...dailyTotals, 1);

  const formatShort = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return `${n}`;
  };

  return (
    <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
      <View style={styles.header}>
        <View>
          <Text style={[styles.title, { color: theme.colors.onSurface }]}>
            Performance Trend
          </Text>
          <Text style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}>
            Earnings this week · UGX
          </Text>
        </View>
        <View style={[styles.badge, { backgroundColor: theme.colors.secondaryContainer }]}>
          <Text style={[styles.badgeText, { color: theme.colors.onSecondaryContainer }]}>
            This week
          </Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
        </View>
      ) : (
        <View style={styles.chartArea}>
          {/* Y-axis reference line */}
          <View style={[styles.gridLine, { borderColor: 'rgba(195,198,213,0.25)' }]} />
          <View style={[styles.gridLine, styles.gridLineMid, { borderColor: 'rgba(195,198,213,0.25)' }]} />

          <View style={styles.barsRow}>
            {dailyTotals.map((val, i) => {
              const isToday = i === todayIso;
              const barHeight = val > 0
                ? Math.max(MIN_BAR_HEIGHT, Math.round((val / maxVal) * MAX_BAR_HEIGHT))
                : MIN_BAR_HEIGHT;

              return (
                <View key={i} style={styles.barColumn}>
                  {/* Value label above bar */}
                  {val > 0 && (
                    <Text style={[
                      styles.barValueLabel,
                      { color: isToday ? theme.colors.primary : theme.colors.onSurfaceVariant },
                    ]}>
                      {formatShort(val)}
                    </Text>
                  )}
                  <View style={styles.barTrack}>
                    <View
                      style={[
                        styles.bar,
                        {
                          height: barHeight,
                          backgroundColor: isToday
                            ? theme.colors.primary
                            : val > 0
                            ? theme.colors.secondaryContainer
                            : 'rgba(195,198,213,0.3)',
                          borderRadius: val > 0 ? 4 : 2,
                        },
                      ]}
                    />
                  </View>
                  <Text style={[
                    styles.dayLabel,
                    {
                      color: isToday ? theme.colors.primary : theme.colors.onSurfaceVariant,
                      fontFamily: isToday ? 'Inter-Medium' : 'Inter-Regular',
                    },
                  ]}>
                    {DAY_LABELS[i]}
                  </Text>
                  {isToday && (
                    <View style={[styles.todayDot, { backgroundColor: theme.colors.primary }]} />
                  )}
                </View>
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 20,
    marginBottom: 12,
    borderRadius: 6,
    padding: 20,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.04,
    shadowRadius: 12,
    elevation: 0,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  title: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 16,
    marginBottom: 2,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
  },
  badge: {
    borderRadius: 9999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  badgeText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  chartArea: {
    position: 'relative',
  },
  gridLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderStyle: 'dashed',
    bottom: 28,
  },
  gridLineMid: {
    bottom: 28 + MAX_BAR_HEIGHT / 2,
  },
  barsRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: MAX_BAR_HEIGHT + 50, // bars + label space
    paddingTop: 20, // room for value labels
  },
  barColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  barValueLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 9,
    marginBottom: 3,
    textAlign: 'center',
  },
  barTrack: {
    width: '70%',
    height: MAX_BAR_HEIGHT,
    justifyContent: 'flex-end',
  },
  bar: {
    width: '100%',
  },
  dayLabel: {
    fontSize: 11,
    marginTop: 6,
  },
  todayDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 2,
  },
});

export default PerformanceTrendCard;
